import { Component, Input } from '@angular/core';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { Observable, combineLatest, map } from 'rxjs';
import { RevisionStore } from 'src/app/stores/component/revision-store';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { debug, RxJsLoggingLevel } from 'src/app/shared/rxjs-operators/debug-operator';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { resizeImageTo1920 } from 'src/app/shared/helpers/image-scaling';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-revisions-detail-documents',
    templateUrl: './revisions-detail-documents.component.html',
    styleUrl: './revisions-detail-documents.component.scss',
    standalone: false
})
export class RevisionsDetailDocumentsComponent {
  @Input() revisionId;
  @Input() revisionReportDocumentId;
  @Input() tenantOnRevision;
  @Input() revisionStatus;

  vm$: Observable<any>;

  public documentDeletionDialogOpened = false;
  public reportDeletionDialogOpened = false;
  public selectedItemToDelete: any = null;

  constructor(
    private translocoService: TranslocoService,
    private revisionStore: RevisionStore,
    private attachmentStore: AttachmentStore,
    private attachmentService: AttachmentService
  ) {
    this.vm$ = combineLatest([ 
      this.attachmentStore.documents$,
      this.attachmentStore.isLoading$,
      this.revisionStore.loading$,
      this.revisionStore.selectedRevision$
    ]).pipe(
      map(([
        documents,
        isUploading,
        generatingReport,
        revision,
      ]) => ({
        documents,
        isUploading,
        generatingReport,
        revision
      }))
    );

  }

  ngOnInit() {
    this.attachmentStore.getAttachmentsByRevision({revisionId: this.revisionId, attachmentId: this.revisionReportDocumentId});
  }

  revisionRestrictions: FileRestrictions = {
    allowedExtensions: [".pdf"],
  };

  generateRevisionReport() 
  {
    this.revisionStore.createRevisionReport({revisionId: this.revisionId});
  }

  public async uploadRevisionAttachment(event: UploadEvent, id: string): Promise<void> {
    event.preventDefault();

    // Create an array to store all FormData objects
    const formDataArray: FormData[] = [];

    // Loop through the files and upload each one
    for (const file of event.files) {
      const formData = new FormData();

      // Resize image
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });
            
      formData.append('file', processedFile, file.name);

      // Append optional IDs to FormData if they are provided
      if (id) {
        formData.append('revisionId', id);
      }

      formData.append('tenantId', this.tenantOnRevision);

      formDataArray.push(formData);
    }
  
    // Call the new addAttachments effect with all files at once
    if (formDataArray.length > 0) {
      this.attachmentStore.addAttachments(formDataArray);
    }
  }

  
  public downloadAttachment(id: string, filename?: string) {
    const fileNameToUse = filename ?? this.translocoService.translate('revisions.detail.revisionReport');
    this.attachmentService.download({attachmentId: id}).subscribe((response) => downloadBlob(response, fileNameToUse));
  }

  public deleteRevisionReport(id: string) {
    this.revisionStore.deleteRevisionReport(this.revisionId);
  }

  public deleteAttachment(id: string) {
    this.attachmentStore.removeAttachmentById(id);
  }

  // delete document confirmation dialog
  public documentDeletionDialogClose(status: string): void {
    this.documentDeletionDialogOpened = false;

    if(status == 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete)
    }

    this.selectedItemToDelete = null;
  }

  public documentDeletionDialogOpen(id: string): void {
    this.documentDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }

  // delete report confirmation dialog
  public reportDeletionDialogClose(status: string): void {
    this.reportDeletionDialogOpened = false;

    if(status == 'delete' && this.selectedItemToDelete) {
      this.deleteRevisionReport(this.selectedItemToDelete)
    }

    this.selectedItemToDelete = null;
  }

  public reportDeletionDialogOpen(id: string): void {
    this.reportDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }  
}
